// src/pages/User/VacationForm.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { FaDownload, FaEdit, FaEye, FaLock, FaTrash } from "react-icons/fa";
import CompanyAddress from "../../components/CompanyAddress";
import LoadingOverlay from "../../components/LoadingOverlay";
import PendingAttachmentList from "../../components/PendingAttachmentList";
import { buildProtectedUploadUrl } from "../../utils/secureFileAccess";
import logo from "../../images/logo.png";
import "../../styles/VacationForm.css";

const VacationForm = () => {
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [vacationTypes, setVacationTypes] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [form, setForm] = useState({
    vf_type: "",
    vf_manager: "",
    vf_from_date: "",
    vf_to_date: "",
  });

  const [editingRow, setEditingRow] = useState(null); // Track which row is being edited
  const [editData, setEditData] = useState({}); // Track editable values
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchFileBlob = useCallback(
    async (url) => {
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`File request failed with status ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(
          errorPayload?.message || "File endpoint returned JSON instead of a file",
        );
      }

      return response.blob();
    },
    [getAuthHeaders],
  );

  const handleFileAction = useCallback(
    async (rawUrl, mode, filename) => {
      const securedUrl = buildProtectedUploadUrl(rawUrl, { mode, filename });

      try {
        const blob = await fetchFileBlob(securedUrl);
        const blobUrl = URL.createObjectURL(blob);

        if (mode === "view") {
          window.open(blobUrl, "_blank", "noopener,noreferrer");
          window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
          return;
        }

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename || "download";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (err) {
        console.error(`Failed to ${mode} file`, err);
        window.alert(`Failed to ${mode} file. Please try again.`);
      }
    },
    [fetchFileBlob],
  );

  // ✅ Fix timezone shift by parsing date-only strings safely
  const toInputDate = (dateString) => {
    if (!dateString) return "";
    // Extract only the date portion (YYYY-MM-DD)
    return dateString.split("T")[0];
  };

  const fetchVacationForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/vacationform/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setUploadedFiles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, userId]);

  const fetchDropdowns = useCallback(async () => {
    try {
      const [vtRes, mgrRes] = await Promise.all([
        axios.get(`${API_BASE}/api/dropdowns/vacation-type`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        axios.get(`${API_BASE}/api/dropdowns/recruiters`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);
      setVacationTypes(vtRes.data);
      setRecruiters(mgrRes.data);
    } catch (err) {
      console.error("Dropdown fetch failed", err);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchVacationForms();
    fetchDropdowns();
  }, [fetchVacationForms, fetchDropdowns]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const extension = selected.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "msg"];

    if (allowedExtensions.includes(extension)) {
      setFile(selected);
    } else {
      alert("Incorrect file format.");
      setFile(null);
      setFileInputKey((value) => value + 1);
    }
  };

  const removePendingAttachment = () => {
    setFile(null);
    setFileInputKey((value) => value + 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fromDate = new Date(form.vf_from_date);
    const toDate = new Date(form.vf_to_date);
    if (fromDate > toDate) {
      alert("From Date must be earlier than To Date.");
      return;
    }

    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("userId", userId);
    formData.append("vf_type", form.vf_type);
    formData.append("vf_manager", form.vf_manager);
    formData.append("vf_from_date", form.vf_from_date);
    formData.append("vf_to_date", form.vf_to_date);

    try {
      await axios.post(
        `${API_BASE}/api/vacationform/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("Vacation request saved as Pending.");
      setFile(null);
      setFileInputKey((value) => value + 1);
      setForm({
        vf_type: "",
        vf_manager: "",
        vf_from_date: "",
        vf_to_date: "",
      });
      await fetchVacationForms();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Upload failed. Please try again.");
    }
  };

  const handleSendForApproval = async (vf_id) => {
    const confirmed = window.confirm(
      "Send this vacation request to the selected manager for approval? You will not be able to edit or delete it after sending."
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      await axios.put(`${API_BASE}/api/vacationform/review/${vf_id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      await fetchVacationForms();
      alert("Vacation request sent for approval.");
    } catch (err) {
      console.error("Failed to send vacation request for approval", err);
      alert(err.response?.data?.message || "Failed to send for approval.");
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (vf_id, decision) => {
    const label = decision === "approved" ? "Approve" : "Reject";
    if (!window.confirm(`${label} this vacation request?`)) return;

    try {
      setLoading(true);
      const endpoint = decision === "approved" ? "complete" : "reject";
      await axios.put(
        `${API_BASE}/api/vacationform/review/${endpoint}/${vf_id}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      await fetchVacationForms();
      alert(`Vacation request ${decision}. The employee has been notified.`);
    } catch (err) {
      console.error(`Failed to mark vacation request as ${decision}`, err);
      alert(err.response?.data?.message || `Failed to ${decision === "approved" ? "approve" : "reject"} request.`);
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing (discard changes)
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleCancelForm = async (vf_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to cancel this vacation request? This cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE}/api/vacationform/cancel/${vf_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      alert("🗑️ Vacation request canceled successfully");
      fetchVacationForms();
    } catch (err) {
      console.error("❌ Cancel failed:", err);
      alert(
        err.response?.data?.message ||
          "Failed to cancel vacation form. Please try again."
      );
    }
  };

  // ✅ Handle Edit / Save logic
  const handleEditClick = (f) => {
    setEditingRow(f.vf_id);
    setEditData({
      vf_type: f.vf_type,
      vf_status: f.vf_status,
      vf_from_date: f.vf_from_date,
      vf_to_date: f.vf_to_date,
      vf_manager: f.manager_id,
      vf_form: null,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileEditChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setEditData((prev) => ({ ...prev, vf_form: selected }));
    }
  };

  const handleSaveClick = async (vf_id) => {
    const formData = new FormData();
    formData.append("vf_type", editData.vf_type);
    if (isAdmin) formData.append("vf_status", editData.vf_status);
    formData.append("vf_from_date", toInputDate(editData.vf_from_date));
    formData.append("vf_to_date", toInputDate(editData.vf_to_date));
    formData.append("vf_manager", editData.vf_manager);
    if (editData.vf_form) formData.append("vf_form", editData.vf_form);

    try {
      await axios.put(
        `${API_BASE}/api/vacationform/update/${vf_id}?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("✔️ Vacation form updated successfully!");
      setEditingRow(null);
      fetchVacationForms();
    } catch (err) {
      console.error("❌ Update failed:", err);
      alert(
        err.response?.data?.message || "Failed to update vacation form.",
      );
    }
  };

  return (
    <>
      {loading && <LoadingOverlay text="Saving changes..." />}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Vacation Form
        </span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Vacation Form</h2>
          </div>
          <CompanyAddress />
        </div>
        <form onSubmit={handleSubmit} className="mt-3">
          {/* Upload new form section */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label>Vacation Type *</label>
              <select
                className="form-select"
                name="vf_type"
                value={form.vf_type}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {vacationTypes.map((vt) => (
                  <option key={vt.vt_id} value={vt.vt_id}>
                    {vt.vt_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label>Manager *</label>
              <Select
                name="vf_manager"
                value={
                  recruiters.find(
                    (r) => r.value === parseInt(form.vf_manager)
                  ) || null
                }
                onChange={(selected) =>
                  setForm((prev) => ({
                    ...prev,
                    vf_manager: selected?.value || "",
                  }))
                }
                options={recruiters}
                placeholder="Select Manager"
                isSearchable
                required
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <label>From Date *</label>
              <input
                type="date"
                name="vf_from_date"
                className="form-control"
                value={form.vf_from_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label>To Date *</label>
              <input
                type="date"
                name="vf_to_date"
                className="form-control"
                value={form.vf_to_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <label>Attachment:</label>
            <input
              key={fileInputKey}
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.msg"
              onChange={handleFileChange}
            />
            <PendingAttachmentList
              files={file ? [{ file, type: file.type || "Attachment" }] : []}
              onRemove={removePendingAttachment}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        </form>

        {/* ✅ Vacation List */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Vacation Requests</div>
            <div className="vacation-table-scroll" role="region" aria-label="Vacation requests" tabIndex="0">
            <table className="table table-sm vacation-request-table" data-no-enhance-table="true">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee ID</th>
                  <th>Employee</th>
                  <th>Vacation Type</th>
                  <th>Manager</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Submitted Date</th>
                  <th>Attachment</th>
                  <th>Status</th>
                  <th>Send for Approval</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((f, index) => (
                  <tr
                    key={f.vf_id}
                    className={
                      f.vf_status === "completed"
                        ? f.vacation_decision === "rejected"
                          ? "table-danger"
                          : "table-success"
                        : f.vf_status === "review"
                        ? "table-warning"
                        : ""
                    }
                  >
                    <td>{index + 1}</td>
                    <td>{f.userid_no}</td>
                    <td>{f.employee_name}</td>
                    <td>
                      {editingRow === f.vf_id ? (
                        <select name="vf_type" className="form-select form-select-sm" value={editData.vf_type || ""} onChange={handleEditChange}>
                          {vacationTypes.map((vt) => <option key={vt.vt_id} value={vt.vt_id}>{vt.vt_name}</option>)}
                        </select>
                      ) : (
                        f.vacation_type || "—"
                      )}
                    </td>
                    <td>
                      {editingRow === f.vf_id ? (
                        <Select
                          value={
                            recruiters.find(
                              (r) => r.value === Number(editData.vf_manager)
                            ) || null
                          }
                          onChange={(selected) =>
                            setEditData((prev) => ({
                              ...prev,
                              vf_manager: selected?.value || "",
                            }))
                          }
                          options={recruiters}
                          isSearchable
                          placeholder="Select Manager"
                        />
                      ) : (
                        f.manager_name || "—"
                      )}
                    </td>

                    <td>
                      {editingRow === f.vf_id ? (
                        <input
                          type="date"
                          name="vf_from_date"
                          value={toInputDate(editData.vf_from_date)}
                          onChange={handleEditChange}
                          className="form-control form-control-sm"
                        />
                      ) : (
                        toInputDate(f.vf_from_date)
                      )}
                    </td>

                    <td>
                      {editingRow === f.vf_id ? (
                        <input
                          type="date"
                          name="vf_to_date"
                          value={toInputDate(editData.vf_to_date)}
                          onChange={handleEditChange}
                          className="form-control form-control-sm"
                        />
                      ) : (
                        toInputDate(f.vf_to_date)
                      )}
                    </td>

                    <td>{toInputDate(f.vf_submitted_date)}</td>
                    <td>
                      {editingRow === f.vf_id ? (
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.msg"
                          onChange={handleFileEditChange}
                        />
                      ) : f.vf_form ? (
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                            title="View file"
                            onClick={() =>
                              handleFileAction(
                                `${API_BASE}/uploads/vacationforms/${f.vf_form}`,
                                "view",
                                f.vf_form_original || f.vf_form,
                              )
                            }
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                            title="Download file"
                            onClick={() =>
                              handleFileAction(
                                `${API_BASE}/uploads/vacationforms/${f.vf_form}`,
                                "download",
                                f.vf_form_original || f.vf_form,
                              )
                            }
                          >
                            <FaDownload />
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">No file</span>
                      )}
                    </td>

                    <td>
                      {editingRow === f.vf_id && isAdmin ? (
                        <select name="vf_status" className="form-select form-select-sm" value={editData.vf_status || f.vf_status} onChange={handleEditChange}>
                          <option value={f.vf_status}>
                            {f.vf_status === "not_completed" ? "Pending" : f.vf_status === "review" ? "Sent" : f.vacation_decision === "rejected" ? "Rejected" : "Approved"}
                          </option>
                          {f.vf_status === "completed" && <option value="review">Sent</option>}
                        </select>
                      ) : f.vf_status === "not_completed" ? (
                        "Pending"
                      ) : f.vf_status === "review" ? (
                        "Sent"
                      ) : f.vacation_decision === "rejected" ? (
                        "Rejected"
                      ) : (
                        "Approved"
                      )}
                    </td>

                    <td>
                      <div className="vacation-approval-actions">
                        {isAdmin ? (
                          f.vf_status === "review" ? (
                            <>
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDecision(f.vf_id, "rejected")} disabled={loading}>Reject</button>
                              <button type="button" className="btn btn-sm btn-success" onClick={() => handleDecision(f.vf_id, "approved")} disabled={loading}>Approve</button>
                            </>
                          ) : f.vf_status === "not_completed" ? (
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleSendForApproval(f.vf_id)} disabled={loading}>Send for Approval</button>
                          ) : (
                            <span className={`vacation-decision-badge ${f.vacation_decision === "rejected" ? "rejected" : "approved"}`}>
                              {f.vacation_decision === "rejected" ? "Rejected" : "Approved"}
                            </span>
                          )
                        ) : f.vf_status === "not_completed" ? (
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => handleSendForApproval(f.vf_id)} disabled={loading}>Send for Approval</button>
                        ) : (
                          <button type="button" className="btn btn-sm btn-secondary" disabled>
                            {f.vf_status === "review" ? "Sent" : f.vacation_decision === "rejected" ? "Rejected" : "Approved"}
                          </button>
                        )}
                      </div>
                    </td>

                    <td>
                      {editingRow === f.vf_id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-success me-2"
                            onClick={() => handleSaveClick(f.vf_id)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <div className="vacation-row-actions">
                          <button
                            type="button"
                            className="btn btn-warning btn-sm vacation-icon-button"
                            disabled={!isAdmin && f.vf_status !== "not_completed"}
                            onClick={() => handleEditClick(f)}
                            title="Edit vacation request"
                            aria-label="Edit vacation request"
                          >
                            <FaEdit aria-hidden="true" />
                          </button>
                          {isAdmin || f.vf_status === "not_completed" ? (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm vacation-icon-button"
                              onClick={() => handleCancelForm(f.vf_id)}
                              title="Delete vacation request"
                              aria-label="Delete vacation request"
                            >
                              <FaTrash aria-hidden="true" />
                            </button>
                          ) : (
                            <span
                              className="vacation-action-locked"
                              title="Sent requests cannot be deleted"
                            >
                              <FaLock aria-hidden="true" />
                              <span>Locked</span>
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default VacationForm;



